import React, { useEffect, useState } from 'react';

import { CircularProgress } from '@mui/material';
import { OPERATION_TYPES } from '../../shared/constants/fiscalFlowTransaction';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Text from '../../components/atoms/Text';
import { getPrefixMonth } from '../../shared/utils/dates.util';
import { DATES } from '../../shared/utils/dates';
import { getDeductibleResumeByFFTId } from '../../services/fft.service';
import { Deductible } from '../../@types/FFT.type';
import { CaretCircleLeft, CheckCircle, XCircle } from '@phosphor-icons/react';
import ROUTES from '../../shared/constants/routes';

const formatPeriod = (period: string) => {
	if (!period) return '';
	const [year, month] = period.split('-');
	const months = [
		'Enero',
		'Febrero',
		'Marzo',
		'Abril',
		'Mayo',
		'Junio',
		'Julio',
		'Agosto',
		'Septiembre',
		'Octubre',
		'Noviembre',
		'Diciembre',
	];
	return `${months[parseInt(month) - 1]} ${year}`;
};

const DetailView: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const id = searchParams.get('id');
	const period = searchParams.get('period');
	const operation_type = searchParams.get('operation_type');
	const home = searchParams.get('home');
	const [counterDeductible, setCounterDeductible] = useState(0);
	const [counterNotDeductible, setCounterNotDeductible] = useState(0);
	const [counterTotal, setCounterTotal] = useState(0);
	const [analysisData, setAnalysisData] = useState<Deductible[]>([]);
	const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);

	const fetchAnalysisData = async () => {
		setIsLoadingAnalysis(true);
		try {
			const response = await getDeductibleResumeByFFTId(Number(id));
			if (
				response?.resource?.statusCode === 500 ||
				!Array.isArray(response?.resource)
			) {
				setAnalysisData([]);
				return;
			}
			setAnalysisData(response?.resource);
		} catch (error) {
			setAnalysisData([]);
		} finally {
			setIsLoadingAnalysis(false);
		}
	};

	useEffect(() => {
		if (!id) return;
		fetchAnalysisData();
	}, [id]);

	useEffect(() => {
		setCounterDeductible(
			analysisData.filter((deductible) => deductible.isInvoiceDeductible)
				.length,
		);
		setCounterNotDeductible(
			analysisData.filter((deductible) => !deductible.isInvoiceDeductible)
				.length,
		);
		setCounterTotal(analysisData.length);
	}, [analysisData]);

	return (
		<div className='p-4'>
			<div className='flex items-start gap-x-2'>
				<div>
					<CaretCircleLeft
						weight='fill'
						color='var(--h-primary)'
						width={24}
						height={24}
						className='cursor-pointer block lg:hidden'
						onClick={() => navigate(home ? ROUTES.ROOT : ROUTES.GENERAL_FFT)}
					/>
				</div>
				<div>
					<Text
						size='body-3'
						weight='bold'
						color='darkBlue'
					>
						{operation_type === OPERATION_TYPES.MONTHLY
							? 'Declaración mensual'
							: 'Declaración anual'}
					</Text>
					<Text
						size='label'
						weight='light'
						color='dark'
					>
						Plataformas tecnológicas, periodo {formatPeriod(period || '')}
					</Text>
					<div className='flex gap-x-2 mb-[48px] mt-[8px]'>
						<Text
							size='mini-1'
							weight='light'
							color='medium'
						>
							Fecha estimada de entrega:
						</Text>
						<Text
							size='mini-1'
							weight='light'
						>
							{`17 al 22 ${getPrefixMonth(DATES.currentMonth)} ${DATES.currentYear}`}
						</Text>
					</div>
				</div>
			</div>

			<Text
				size='caption'
				weight='medium'
				color='medium'
				sx='mb-[16px]'
			>
				Deducciones
			</Text>
			<Text
				size='mini-1'
				weight='light'
				sx='mb-[16px]'
			>
				Aquí encontrarás el detalle de todas tus facturas y deducibles encontrados.
				<br />
				Ten la seguridad de que nuestro sistema analiza cuidadosamente cada una
				para maximizar tus deducciones de manera correcta y segura.
			</Text>
			<div className='flex gap-x-[20px] mb-[24px]'>
				<div className='flex items-center gap-x-2'>
					<div className='w-2.5 h-2.5 bg-[#6aa132] rounded-full' />
					<Text
						size='caption'
						weight='light'
					>
						Deducibles: {counterDeductible}
					</Text>
				</div>
				<div className='flex items-center gap-x-2'>
					<div className='w-2.5 h-2.5 bg-[#da1e28] rounded-full' />
					<Text
						size='caption'
						weight='light'
					>
						No deducibles: {counterNotDeductible}
					</Text>
				</div>
				<div className='flex items-center gap-x-2'>
					<Text
						size='caption'
						weight='light'
					>
						Total: {counterTotal}
					</Text>
				</div>
			</div>
			{isLoadingAnalysis ? (
				<div className='flex justify-center items-center mt-[200px]'>
					<CircularProgress />
				</div>
			) : (
				analysisData.map((deductible, index) => (
					<div
						key={index}
						className={`${deductible.isInvoiceDeductible ? 'border-[#6aa132]' : 'border-[#da1e28]'} p-[16px] border-[1px] rounded-[8px] mb-[8px] border-l-[6px] flex gap-2`}
					>
						<div className='w-6 h-6 flex items-center justify-center flex-shrink-0'>
							{deductible.isInvoiceDeductible ? (
								<CheckCircle
									size={20}
									weight='duotone'
									color='#6aa132'
								/>
							) : (
								<XCircle
									size={20}
									weight='duotone'
									color='#da1e28'
								/>
							)}
						</div>
						<div className='flex flex-col'>
							<Text
								size='mini-1'
								weight='medium'
								color='medium'
							>
								Factura #{deductible?.invoiceId}
							</Text>
							{deductible.items &&
								deductible.items.map((item, idx) => (
									<div key={idx}>
										<Text
											size='mini-1'
											weight='light'
											color='medium'
										>
											{item.description}
										</Text>
										<Text
											size='mini-1'
											weight='light'
											color='medium'
											sx='mt-[8px]'
										>
											Razón: {item.reason}
										</Text>
									</div>
								))}
						</div>
					</div>
				))
			)}
		</div>
	);
};

export default DetailView;
